import {
  CountrySelectorForHeader,
  AutocompleteWithLabel,
  InputWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect, useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { CITIES_QUERY } from '../../graphql/cities'
import {
  countries,
  countryToFlag,
  CountryType
} from '../../helpers/countriesList'
import { isFaAvailableInLanguages } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { City } from '../../types/cities'

interface AccommodationAddressDetailsProps {
  values: Accommodation
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<Accommodation>
  touched: FormikTouched<Accommodation>
  handleBlur: any
  isSubmitting: any
  classes: any
  loading: boolean
}

export const AccommodationAddressDetails: React.FC<
  AccommodationAddressDetailsProps
> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes,
  loading
}) => {
  const [selectedCountryForAddress, setselectedCountryForAddress] =
    useState<CountryType>(countries[0])

  const { data: citiesList, loading: loadingForCities } = useQuery<{
    cities: City[]
  }>(CITIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  const addressLanguage =
    selectedCountryForAddress?.nativeLanguage && lang !== 'en'
      ? selectedCountryForAddress.nativeLanguage
      : selectedCountryForAddress?.language
  const [autoCompleteLabelCountry, setautoCompleteLabelCountry] = useState<any>(
    {
      label: '',
      value: '',
      id: '',
      code: ''
    }
  )

  const [selectedCity, setselectedCity] = useState<any>({
    label: '',
    value: '',
    id: '',
    code: ''
  })

  useEffect(() => {
    if (
      citiesList?.cities &&
      citiesList?.cities.length > 0 &&
      values?.addressDetails?.city
    ) {
      const newSelectedCity = citiesList?.cities.find(
        (singleCity) => singleCity.cityID === values?.addressDetails?.city
      )
      if (newSelectedCity) {
        setselectedCity({
          label:
            lang === 'en' ? newSelectedCity.nameEn : newSelectedCity.nameFa,
          value: newSelectedCity.cityID,
          code: newSelectedCity.cityID
        })
      }
    }
  }, [citiesList, values?.addressDetails?.city])

  useEffect(() => {
    if (
      !loading &&
      countries.find((country) =>
        country.label
          .toLocaleLowerCase()
          .includes(values?.addressDetails?.country.label.toLocaleLowerCase())
      )?.label
    ) {
      const selectedCountry = countries.find((country) =>
        country.label
          .toLocaleLowerCase()
          .includes(values?.addressDetails?.country.label.toLocaleLowerCase())
      )

      if (selectedCountry) {
        const temp = {
          value: selectedCountry.code,
          label: selectedCountry.label,
          id: selectedCountry.code,
          code: selectedCountry.phone
        }
        setautoCompleteLabelCountry(temp)
        setFieldValue('addressDetails.country', temp)
      }
    } else {
      setautoCompleteLabelCountry(null)
    }
  }, [loading])

  return (
    <>
      <SectionTitle title="Address Details" marginBottom />
      <div className={classes.fieldsContainer}>
        <AutocompleteWithLabel
          includeInputInList
          name="addressDetails.country"
          label={getTranslate('Country')}
          placeholder={getTranslate('Select accommodation country')}
          value={autoCompleteLabelCountry}
          onChange={(_, value: any) => {
            const temp = {
              label: value?.label || '',
              value: value?.value || '',
              id: value?.id || '',
              code: value?.code || ''
            }
            setFieldValue('addressDetails.country', temp)
            setautoCompleteLabelCountry(value?.label ? temp : null)
          }}
          getOptionLabel={(option) =>
            option.label || getTranslate('Loading') + '...'
          }
          getOptionSelected={(option, value) => option.value === value.value}
          options={countries.map((item) => ({
            label: item.label,
            value: item.code,
            id: item.code,
            code: item.phone
          }))}
          renderOption={(option) => (
            <>
              <span>{countryToFlag(option.id)}</span>&nbsp;
              {option.label} ({option.id}) + {option.code}
            </>
          )}
          size="small"
          error={
            (touched.addressDetails?.country &&
              !!errors.addressDetails?.country) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.country
              ? getTranslate(errors.addressDetails?.country?.code || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          loading={loading}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          data-cy="addressDetailsCity"
          name="addressDetails.city"
          label={getTranslate('City')}
          getOptionLabel={(option) => option.label || ''}
          placeholder={getTranslate('Select accommodation city')}
          value={selectedCity}
          onChange={(_, value: any) => {
            const temp = {
              label: value?.label,
              value: value?.code,
              id: value?.id,
              code: value?.code
            }
            setFieldValue('addressDetails.city', temp.value)
            setselectedCity(value?.label ? temp : null)
          }}
          getOptionSelected={(option, value) => option.value === value.value}
          options={[
            ...(citiesList?.cities.map((item) => ({
              label: lang === 'en' ? item.nameEn : item.nameFa,
              value: item.cityID,
              id: item.cityID,
              code: item.cityID
            })) || [])
          ]}
          size="small"
          error={
            (touched.addressDetails?.city && !!errors.addressDetails?.city) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.city
              ? getTranslate(errors.addressDetails?.city || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          loading={loadingForCities}
          loadingText={getTranslate('Loading') + '...'}
        />
        <InputWithLabel
          data-cy="addressDetailsPostalCode"
          name="addressDetails.postalCode"
          label={getTranslate('Zip/Postal Code')}
          placeholder={getTranslate('Enter the accommodation Zip/Postal code')}
          autoComplete="off"
          value={values?.addressDetails?.postalCode}
          onChange={(event) =>
            setFieldValue(
              'addressDetails.postalCode',
              event.currentTarget.value
            )
          }
          error={
            (touched.addressDetails?.postalCode &&
              !!errors.addressDetails?.postalCode) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.postalCode
              ? getTranslate(errors.addressDetails?.postalCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="addressDetailsAddress"
          name="addressDetails.address"
          label={getTranslate('Address')}
          placeholder={getTranslate('Enter the accommodation full address')}
          autoComplete="off"
          value={values?.addressDetails?.address}
          onChange={(event) =>
            setFieldValue('addressDetails.address', event.currentTarget.value)
          }
          error={
            (touched.addressDetails?.address &&
              !!errors.addressDetails?.address) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.address
              ? getTranslate(errors.addressDetails?.address || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        {isFaAvailableInLanguages() && (
          <InputWithLabel
            data-cy="addressDetailsPersianAddress"
            label={`${lang === 'en' ? addressLanguage : ''} ${getTranslate(
              'Address'
            )} ${lang === 'fa' ? addressLanguage : ''}`}
            placeholder={`${getTranslate(
              'Enter the accommodation address in'
            )} ${addressLanguage}`}
            autoComplete="off"
            id={`addressDetails.addressTranslatedList[${values?.addressDetails?.addressTranslatedList.findIndex(
              (item) => item.label === selectedCountryForAddress.label
            )}].value`}
            name={`addressDetails.addressTranslatedList[${values?.addressDetails?.addressTranslatedList.findIndex(
              (item) => item.label === selectedCountryForAddress.label
            )}].value`}
            value={
              values?.addressDetails?.addressTranslatedList.find(
                (item) => item.label === selectedCountryForAddress.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `addressDetails.addressTranslatedList[${values?.addressDetails?.addressTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForAddress.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            error={
              (touched.addressDetails?.addressTranslatedList &&
                !values?.addressDetails?.addressTranslatedList[0].value) ||
              undefined
            }
            helpertext={
              touched.addressDetails?.addressTranslatedList &&
              !values?.addressDetails?.addressTranslatedList[0].value
                ? getTranslate('This field is required')
                : ''
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForAddress}
                onChange={(item: CountryType) => {
                  const translatedAddress = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values?.addressDetails?.addressTranslatedList,
                    values?.addressDetails?.addressTranslatedList.find(
                      (singleAddress) => singleAddress.label === item.label
                    )
                      ? null
                      : translatedAddress
                  ]
                  setFieldValue(
                    'addressDetails.addressTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForAddress(item)
                }}
              />
            }
          />
        )}
        <InputWithLabel
          name="addressDetails.latitude"
          label={getTranslate('Latitude')}
          placeholder={getTranslate('Enter the latitude')}
          autoComplete="off"
          value={values?.addressDetails?.latitude}
          onChange={(event) =>
            setFieldValue(
              'addressDetails.latitude',
              parseFloat(event.currentTarget.value)
            )
          }
          error={
            (touched.addressDetails?.latitude &&
              !!errors.addressDetails?.latitude) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.latitude
              ? getTranslate(errors.addressDetails?.latitude || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          name="addressDetails.longitude"
          label={getTranslate('Longitude')}
          placeholder={getTranslate('Enter the longitude')}
          autoComplete="off"
          value={values?.addressDetails?.longitude}
          onChange={(event) =>
            setFieldValue(
              'addressDetails.longitude',
              parseFloat(event.currentTarget.value)
            )
          }
          error={
            (touched.addressDetails?.longitude &&
              !!errors.addressDetails?.longitude) ||
            undefined
          }
          helpertext={
            touched.addressDetails?.longitude
              ? getTranslate(errors.addressDetails?.longitude || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
      </div>
    </>
  )
}
